import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Tooltip,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import Overlay from "components/Overlay/Overlay";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import InputRoundedIcon from "@mui/icons-material/InputRounded";
import SmartButtonRoundedIcon from "@mui/icons-material/SmartButtonRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import ViewQuiltRoundedIcon from "@mui/icons-material/ViewQuiltRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import dayjs from "dayjs";

const OverlayShowcase = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { setOverlayName } = useStateContext();

  const [activeTab, setActiveTab] = useState(0);
  const [showcaseOpen, setShowcaseOpen] = useState(true);
  const [sampleSwitch, setSampleSwitch] = useState(true);
  const [sampleSelect, setSampleSelect] = useState("option1");
  const [sampleDate, setSampleDate] = useState(dayjs());
  const [headerSelectValue, setHeaderSelectValue] = useState("showcase");

  const handleClose = () => {
    setShowcaseOpen(false);
    setOverlayName(null);
  };

  // ── Tab definitions ────────────────────────────────────────────────
  const tabs = [
    {
      label: "Form Fields",
      icon: <InputRoundedIcon />,
      content: <FormFieldsTab />,
    },
    {
      label: "Buttons",
      icon: <SmartButtonRoundedIcon />,
      content: <ButtonsTab />,
    },
    {
      label: "Warnings",
      icon: <WarningRoundedIcon />,
      content: <WarningsTab />,
    },
    {
      label: "Layout",
      icon: <ViewQuiltRoundedIcon />,
      content: <LayoutTab />,
    },
  ];

  // ── Header actions ─────────────────────────────────────────────────
  const headerActions = (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      <Tooltip title="Duplicate">
        <IconButton size="small" onClick={() => console.log("Duplicate clicked")}>
          <ContentCopyRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Share">
        <IconButton size="small" onClick={() => console.log("Share clicked")}>
          <ShareRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="More actions">
        <IconButton size="small" onClick={() => console.log("More clicked")}>
          <MoreVertRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // ── Header select ──────────────────────────────────────────────────
  const headerSelect = (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <Select
        value={headerSelectValue}
        onChange={(e) => setHeaderSelectValue(e.target.value)}
        sx={{
          bgcolor: theme.palette.background.default,
          borderRadius: "8px",
          fontSize: "0.85rem",
        }}
      >
        <MenuItem value="showcase">Showcase</MenuItem>
        <MenuItem value="template">Template</MenuItem>
        <MenuItem value="preview">Preview</MenuItem>
      </Select>
    </FormControl>
  );

  // ── Warnings (dynamic per tab) ─────────────────────────────────────
  const tabWarnings = [
    [
      {
        type: "info",
        message: "📝 Form Fields tab — Info: This tab shows various form input types.",
        icon: <InfoRoundedIcon />,
      },
    ],
    [
      {
        type: "warning",
        message: "🔘 Buttons tab — Warning: Button styles and variants demonstrated here.",
        icon: <WarningAmberRoundedIcon />,
      },
    ],
    [
      {
        type: "error",
        message: "⚠️ Warnings tab — Error: This tab showcases the warning/info/error bars.",
        icon: <ErrorOutlineRoundedIcon />,
      },
    ],
    [
      {
        type: "info",
        message: "📐 Layout tab — Info: Grid layouts and spacing examples.",
        icon: <InfoRoundedIcon />,
      },
    ],
  ];

  const warnings = tabWarnings[activeTab] || [];

  // ── Footer ─────────────────────────────────────────────────────────
  const footer = (
    <>
      <Typography
        variant="body2"
        sx={{ color: theme.palette.text.secondary, fontStyle: "italic" }}
      >
        Showcase overlay — no changes will be persisted
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        {isMobile ? (
          <>
            <Tooltip title="Discard">
              <IconButton
                onClick={() => console.log("Discard clicked")}
                sx={{ color: theme.palette.text.secondary }}
              >
                <DeleteOutlineRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Save">
              <IconButton
                onClick={() => console.log("Save clicked")}
                sx={{ color: theme.palette.primary.main }}
              >
                <SaveRoundedIcon />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Button
              variant="text"
              onClick={() => console.log("Discard clicked")}
              sx={{ textTransform: "none", color: theme.palette.text.secondary }}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveRoundedIcon />}
              onClick={() => console.log("Save clicked")}
              sx={{ textTransform: "none" }}
            >
              Save
            </Button>
          </>
        )}
      </Box>
    </>
  );

  return (
    <Overlay
      id="overlay-showcase"
      open={showcaseOpen}
      onClose={handleClose}
      title="Overlay Showcase"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      headerActions={headerActions}
      headerSelect={headerSelect}
      warnings={warnings}
      footer={footer}
      maxWidth="lg"
      scrollable
    >
      {/* Fallback content when no tabs */}
      <Box sx={{ p: 3 }}>
        <Typography>No tab content available.</Typography>
      </Box>
    </Overlay>
  );
};

// ─── Tab 1: Form Fields ──────────────────────────────────────────────────────
const FormFieldsTab = () => {
  const theme = useTheme();
  const [sampleSwitch, setSampleSwitch] = useState(true);
  const [sampleSelect, setSampleSelect] = useState("option1");
  const [sampleDate, setSampleDate] = useState(dayjs());

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ bgcolor: theme.palette.info.main, color: "#fff", p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          📝 Form Fields Tab
        </Typography>
        <Typography variant="body2">
          This tab has an INFO warning bar (blue)
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
        Sample form inputs demonstrating text fields, selects, date pickers, and switches.
      </Typography>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Text Input"
            placeholder="Type something..."
            variant="outlined"
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Email"
            placeholder="user@example.com"
            variant="outlined"
            size="small"
            type="email"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Select Field</InputLabel>
            <Select
              value={sampleSelect}
              onChange={(e) => setSampleSelect(e.target.value)}
              label="Select Field"
            >
              <MenuItem value="option1">Option 1</MenuItem>
              <MenuItem value="option2">Option 2</MenuItem>
              <MenuItem value="option3">Option 3</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DatePicker
            label="Date Picker"
            value={sampleDate}
            onChange={(newDate) => setSampleDate(newDate)}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Number"
            placeholder="0"
            variant="outlined"
            size="small"
            type="number"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Multiline"
            placeholder="Enter multiple lines..."
            variant="outlined"
            size="small"
            multiline
            rows={3}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={sampleSwitch}
                  onChange={(e) => setSampleSwitch(e.target.checked)}
                />
              }
              label="Toggle Switch"
            />
            <FormControlLabel
              control={<Switch defaultChecked disabled />}
              label="Disabled On"
            />
            <FormControlLabel
              control={<Switch disabled />}
              label="Disabled Off"
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
            <Chip label="Default Chip" />
            <Chip label="Primary" color="primary" size="small" />
            <Chip label="Success" color="success" variant="outlined" />
            <Chip label="Error" color="error" size="small" />
            <Chip label="Clickable" onClick={() => console.log("Chip clicked")} clickable />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// ─── Tab 2: Buttons ──────────────────────────────────────────────────────────
const ButtonsTab = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ bgcolor: theme.palette.warning.main, color: "#fff", p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          🔘 Buttons Tab
        </Typography>
        <Typography variant="body2">
          This tab has a WARNING warning bar (orange)
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
        Various button patterns — contained, outlined, text, icon buttons, and responsive adaptations.
      </Typography>

      <Stack spacing={3}>
        {/* Contained buttons */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Contained
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="contained" sx={{ textTransform: "none" }}>
              Primary
            </Button>
            <Button variant="contained" color="secondary" sx={{ textTransform: "none" }}>
              Secondary
            </Button>
            <Button variant="contained" disabled sx={{ textTransform: "none" }}>
              Disabled
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveRoundedIcon />}
              sx={{ textTransform: "none" }}
            >
              With Icon
            </Button>
          </Box>
        </Box>

        {/* Outlined buttons */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Outlined
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" sx={{ textTransform: "none" }}>
              Primary
            </Button>
            <Button variant="outlined" color="error" sx={{ textTransform: "none" }}>
              Danger
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              sx={{ textTransform: "none" }}
            >
              Add New
            </Button>
          </Box>
        </Box>

        {/* Text buttons */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Text
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="text" sx={{ textTransform: "none" }}>
              Default
            </Button>
            <Button variant="text" color="primary" sx={{ textTransform: "none" }}>
              Primary
            </Button>
            <Button variant="text" color="error" sx={{ textTransform: "none" }}>
              Delete
            </Button>
          </Box>
        </Box>

        {/* Icon buttons */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Icon Buttons
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Tooltip title="Add">
              <IconButton color="primary">
                <AddRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton>
                <EditRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton color="error">
                <DeleteRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share">
              <IconButton>
                <ShareRoundedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Mobile vs Desktop */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Responsive (Mobile vs Desktop)
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              bgcolor: theme.palette.background.default,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2">
              Current: <strong>{isMobile ? "Mobile" : "Desktop"}</strong>
            </Typography>
            {isMobile ? (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton size="small" color="primary">
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error">
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
                <IconButton size="small">
                  <ShareRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditRoundedIcon />}
                  sx={{ textTransform: "none" }}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteRoundedIcon />}
                  sx={{ textTransform: "none" }}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ShareRoundedIcon />}
                  sx={{ textTransform: "none" }}
                >
                  Share
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

// ─── Tab 3: Warnings ─────────────────────────────────────────────────────────
const WarningsTab = () => {
  const theme = useTheme();

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ bgcolor: theme.palette.error.main, color: "#fff", p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          ⚠️ Warnings Tab
        </Typography>
        <Typography variant="body2">
          This tab has an ERROR warning bar (red)
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
        Different alert and warning patterns used across the portal.
      </Typography>

      <Stack spacing={2.5}>
        {/* MUI Alerts */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            MUI Alert Component
          </Typography>
          <Stack spacing={1}>
            <Alert severity="info" variant="standard">
              This is an informational alert — useful for general tips.
            </Alert>
            <Alert severity="warning" variant="standard">
              This is a warning alert — something needs attention.
            </Alert>
            <Alert severity="error" variant="standard">
              This is an error alert — something went wrong.
            </Alert>
            <Alert severity="success" variant="standard">
              This is a success alert — operation completed.
            </Alert>
          </Stack>
        </Box>

        {/* Overlay-style warning bars */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Overlay Warning Bars (as seen in footer area)
          </Typography>
          <Box
            sx={{
              borderRadius: "8px",
              overflow: "hidden",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1,
                bgcolor: theme.palette.background.info,
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderBottom: `1px solid ${theme.palette.info?.main || theme.palette.primary.main}`,
              }}
            >
              <InfoRoundedIcon sx={{ color: theme.palette.primary.main, fontSize: "1.1rem" }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Info bar — rendered below content area in the Overlay.
              </Typography>
            </Box>
            <Box
              sx={{
                px: 2,
                py: 1,
                bgcolor: theme.palette.background.warning,
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderBottom: `1px solid ${theme.palette.warning.main}`,
              }}
            >
              <WarningAmberRoundedIcon sx={{ color: theme.palette.warning.main, fontSize: "1.1rem" }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Warning bar — timezone mismatch detected.
              </Typography>
            </Box>
            <Box
              sx={{
                px: 2,
                py: 1,
                bgcolor: theme.palette.background.error,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ErrorOutlineRoundedIcon sx={{ color: theme.palette.error.main, fontSize: "1.1rem" }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                Error bar — required field is missing.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Custom info boxes */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Custom Info Boxes
          </Typography>
          <Stack spacing={1}>
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                bgcolor: theme.palette.background.default,
                borderLeft: `4px solid ${theme.palette.primary.main}`,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Tip: You can use the Overlay component with different props
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Try passing subtitle, contentBg, height, and scrollable props.
              </Typography>
            </Box>
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                bgcolor: theme.palette.background.default,
                borderLeft: `4px solid ${theme.palette.warning.main}`,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Note: This showcase is for demonstration only
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                No data entered here will be persisted.
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

// ─── Tab 4: Layout ───────────────────────────────────────────────────────────
const LayoutTab = () => {
  const theme = useTheme();

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ bgcolor: theme.palette.info.main, color: "#fff", p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          📐 Layout Tab
        </Typography>
        <Typography variant="body2">
          This tab has an INFO warning bar (blue)
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
        Grid layouts, dividers, typography, and spacing patterns.
      </Typography>

      <Stack spacing={3}>
        {/* Grid layout */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Grid Layout
          </Typography>
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.divider}`,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Card {i}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Grid item with responsive sizing
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider />

        {/* Typography */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Typography Scale
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="h2">Heading 2 (h2)</Typography>
            <Typography variant="h4">Heading 4 (h4)</Typography>
            <Typography variant="h5">Heading 5 (h5)</Typography>
            <Typography variant="h6">Heading 6 (h6)</Typography>
            <Typography variant="body1">Body 1 — Regular paragraph text</Typography>
            <Typography variant="body2">Body 2 — Secondary text, captions</Typography>
            <Typography variant="caption">Caption — Smallest text, metadata</Typography>
          </Stack>
        </Box>

        <Divider />

        {/* Spacing & Dividers */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Spacing & Dividers
          </Typography>
          <Stack spacing={1.5}>
            <Box sx={{ p: 1.5, bgcolor: theme.palette.background.default, borderRadius: "8px" }}>
              <Typography variant="body2">Item with padding 1.5</Typography>
            </Box>
            <Divider variant="middle" />
            <Box sx={{ p: 2, bgcolor: theme.palette.background.default, borderRadius: "8px" }}>
              <Typography variant="body2">Item with padding 2</Typography>
            </Box>
            <Divider variant="middle" />
            <Box sx={{ p: 3, bgcolor: theme.palette.background.default, borderRadius: "8px" }}>
              <Typography variant="body2">Item with padding 3</Typography>
            </Box>
          </Stack>
        </Box>

        <Divider />

        {/* Color palette preview */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Theme Palette Preview
          </Typography>
          <Grid container spacing={1.5}>
            {[
              { label: "Primary", color: theme.palette.primary.main },
              { label: "Secondary", color: theme.palette.secondary?.main || "#9c27b0" },
              { label: "Info BG", color: theme.palette.background.info },
              { label: "Warning BG", color: theme.palette.background.warning },
              { label: "Error BG", color: theme.palette.background.error },
              { label: "Alt BG", color: theme.palette.background.alt },
              { label: "Default BG", color: theme.palette.background.default },
            ].map((item) => (
              <Grid key={item.label} size={{ xs: 6, sm: 4, md: 3 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "8px",
                    bgcolor: item.color,
                    border: `1px solid ${theme.palette.divider}`,
                    textAlign: "center",
                    minHeight: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
    </Box>
  );
};

export default OverlayShowcase;
